import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowLeft, GearSix, NotePencil, Plant } from "@phosphor-icons/react";
import { asHTML, asText } from "@prismicio/client";
import { Page } from "components/Page";
import { WithAuthorization } from "context";
import { routes } from "config";
import { useGetAllFaqs, useGetHelpSearchableDocuments } from "hooks/query";
import { SLICE_COMPONENTS } from "./slices";
import { richTextSx } from "./slices/richTextStyles";

// The Help section's landing page IS the "Entry" article authored in Prismic
// (help_article, uid "how-to-use-this-guide") — its own Colums/Link slices are the
// browsing/navigation UI (onboarding vs returning-user paths, module tiles), so
// there's no separate list/search page in between "/app/help" and real content.
const ENTRY_ARTICLE_UID = "how-to-use-this-guide";

// "help_article" has its own top-level `title` field, but it's usually left empty in
// practice — and "help_guide"/"help_hub" don't have one AT ALL (`article.data.title`
// is `null`, not an empty rich text array, for every real document of those two types).
// The heading everyone actually authors lives in the first slice instead (home_hero's
// `primary_heading` for every article we have except one FAQ page, which opens with a
// bodycontent slice's plain-string `heading`). Search needs *a* title for every
// document regardless of type, so this tries the field first, then walks the slices,
// then falls back to the uid — never leaving a document with no display title, which
// is what let `asText(article.data.title).toLowerCase()` crash the moment a document
// with no title field was searched (see git history for the incident this fixed).
function getArticleDisplayTitle(article) {
  const titleFromField = asText(article.data.title);
  if (titleFromField) return titleFromField;

  for (const slice of article.data.slices || []) {
    const heading = slice.primary?.primary_heading ?? slice.primary?.heading;
    if (typeof heading === "string" && heading.trim()) return heading;
    if (Array.isArray(heading)) {
      const text = asText(heading);
      if (text) return text;
    }
  }

  return article.uid.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Prismic bookkeeping keys (ids, slugs, link metadata) rather than human-authored
// content — skipped while walking a slice's fields so search text isn't polluted with
// noise that would produce confusing "why did this match?" results.
const NON_CONTENT_KEYS = new Set([
  "id",
  "uid",
  "key",
  "tags",
  "lang",
  "slug",
  "slugs",
  "type",
  "link_type",
  "url",
  "isBroken",
  "first_publication_date",
  "last_publication_date",
  "variation",
  "version",
  "slice_label",
  "direction",
  "spans",
  "dimensions",
  "edit",
  "orientation",
]);

// A Prismic rich text field is an array of node objects like {type, text, spans, ...}
// (or {type, url, alt, ...} for an embedded image). An empty rich text field is `[]`.
function isPrismicRichText(value) {
  return (
    Array.isArray(value) &&
    (value.length === 0 || value.every((node) => node && typeof node === "object" && "type" in node && ("text" in node || "url" in node || "alt" in node)))
  );
}

// A Prismic Link/Content-Relationship field — a reference to another document (or an
// external URL), not this document's own content. `page_to_link_to`/`article_link`/`cta`
// are all this shape; deliberately not followed into the linked document's own fields.
function isPrismicLink(value) {
  return !!value && typeof value === "object" && !Array.isArray(value) && "link_type" in value;
}

function collectSliceText(value, acc) {
  if (value == null) return;
  if (typeof value === "string") {
    if (value.trim()) acc.push(value);
    return;
  }
  if (isPrismicLink(value)) return;
  if (isPrismicRichText(value)) {
    const text = asText(value);
    if (text) acc.push(text);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectSliceText(item, acc));
    return;
  }
  if (typeof value === "object") {
    Object.entries(value).forEach(([key, v]) => {
      if (!NON_CONTENT_KEYS.has(key)) collectSliceText(v, acc);
    });
  }
}

// Full-text search surface for one article: title, summary, the top-level `body` rich
// text field, and every slice's own authored content (headings, paragraphs, link
// descriptions, "you'll need"/"you'll learn" lists, etc.) — generic over slice shape
// rather than a per-slice-type allowlist, so a new slice type authored in Prismic is
// searchable without a matching code change here. Case-folded once so every call site
// does a plain substring check.
//
// Deliberately does NOT include a "faq" slice's live-resolved Q&A text (it did briefly —
// see git history) — FAQs now get their own results section (faqSearchResults, rendered
// by FaqResultsAccordion) with the actual answer one click away, so also matching them
// into the article list just put the same content on screen twice: e.g. the "faqs" hub
// page itself, or any guide with a "Settings"-tagged FAQ block, would show up as an
// "article" result purely because an embedded FAQ matched, right next to that same FAQ
// already showing its own answer in the FAQs section above.
function getArticleSearchText(article) {
  const parts = [getArticleDisplayTitle(article), article.data.summary || ""];
  const bodyText = asText(article.data.body);
  if (bodyText) parts.push(bodyText);
  (article.data.slices || []).forEach((slice) => {
    collectSliceText(slice.primary, parts);
    collectSliceText(slice.items, parts);
  });
  return parts.join(" ").toLowerCase();
}

function HelpPage() {
  // All searchable Help page types (help_article/"Help Home", help_guide, help_hub) —
  // search results and the currently-selected page are both resolved from this one list.
  const { data: articles, isLoading } = useGetHelpSearchableDocuments();
  // For search only (see collectFaqSliceText) — every "faq" document, so a "faq" slice's
  // live-resolved Q&A text is searchable even though it isn't part of the article itself.
  const { data: allFaqs } = useGetAllFaqs();
  const navigate = useNavigate();
  // Route is `${routes.help}/*` (see paths.js) — the article uid is the trailing path
  // segment, so every article has its own real, linkable/bookmarkable URL instead of
  // living behind in-memory state under one fixed "/app/help". Bare "/app/help" (no
  // trailing segment) is the Entry article.
  const { "*": wildcardPath } = useParams();
  const selectedUid = (wildcardPath || "").split("/")[0] || ENTRY_ARTICLE_UID;
  const [searchQuery, setSearchQuery] = useState("");
  // The page the user was on when they started typing — restored when they back out
  // of search, since search results replace the current page's content rather than
  // navigating to a new one.
  const [returnUid, setReturnUid] = useState(null);
  const isSearching = searchQuery.trim().length > 0;

  // Entry article keeps the bare "/app/help" URL rather than "/app/help/how-to-use-this-guide" —
  // both would resolve to the same content, but the bare form is the one everyone already
  // links to (sidebar, bookmarks) and is nicer to look at.
  const goToArticle = (uid) => {
    navigate(uid && uid !== ENTRY_ARTICLE_UID ? `${routes.help}/${uid}` : routes.help);
  };

  const selectedArticle = useMemo(
    () => (articles || []).find((article) => article.uid === selectedUid),
    [articles, selectedUid]
  );

  // Navigating between Help articles swaps content in place (no route change),
  // so the app's scrollable content container (Layout.jsx's #layoutContainer)
  // keeps whatever scroll position the user was at on the previous article —
  // scroll it back to the top whenever the selected article changes.
  useEffect(() => {
    document.getElementById("layoutContainer")?.scrollTo({ top: 0 });
  }, [selectedUid]);

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const query = searchQuery.trim().toLowerCase();
    return (articles || []).filter((article) => getArticleSearchText(article).includes(query));
  }, [articles, searchQuery, isSearching]);

  // FAQs matching the query are shown as their own accordion list in the results screen
  // (SearchResultsPanel) — not links to an article, since the answer is right there once
  // expanded. A FAQ can appear on several pages (matched by tag) or none at all yet still
  // be worth surfacing here as a direct answer.
  const faqSearchResults = useMemo(() => {
    if (!isSearching) return [];
    const query = searchQuery.trim().toLowerCase();
    return (allFaqs || []).filter((faq) => {
      const question = (faq.data?.question || "").toLowerCase();
      const answer = (asText(faq.data?.answer) || "").toLowerCase();
      return question.includes(query) || answer.includes(query);
    });
  }, [allFaqs, searchQuery, isSearching]);

  const handleSearchChange = (value) => {
    if (!isSearching && value.trim()) {
      setReturnUid(selectedUid);
    }
    setSearchQuery(value);
  };

  const handleExitSearch = () => {
    setSearchQuery("");
    goToArticle(returnUid ?? ENTRY_ARTICLE_UID);
    setReturnUid(null);
  };

  const handleSelectResult = (uid) => {
    setSearchQuery("");
    setReturnUid(null);
    goToArticle(uid);
  };

  return (
    <Page>
      {isLoading && (
        <Box maxW="900px" mx="auto" p={6}>
          <Spinner />
        </Box>
      )}
      {!isLoading && selectedArticle && (
        <HelpArticleDetail
          article={selectedArticle}
          isEntry={selectedUid === ENTRY_ARTICLE_UID}
          onBack={() => goToArticle(ENTRY_ARTICLE_UID)}
          onNavigate={goToArticle}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          isSearching={isSearching}
          searchResults={searchResults}
          faqSearchResults={faqSearchResults}
          onExitSearch={handleExitSearch}
          onSelectResult={handleSelectResult}
        />
      )}
      {!isLoading && !selectedArticle && (
        <Box maxW="900px" mx="auto" p={6}>
          <Text color="gray.500">This help content isn't available right now.</Text>
        </Box>
      )}
    </Page>
  );
}

// Same "Back to Help" link styling, reused for the search results' back link.
function BackLink({ onClick, children }) {
  return (
    <HStack
      as="button"
      onClick={onClick}
      cursor="pointer"
      gap={2}
      color="#5A8A1A"
      fontSize="14px"
      _hover={{ color: "#4a7516" }}
      transition="color 0.15s"
    >
      <ArrowLeft size={16} />
      <Text color="inherit" fontSize="inherit">
        {children}
      </Text>
    </HStack>
  );
}

// Section label above the FAQs/Articles groups within the results panel — a plain
// bold label rather than FaqSlice.jsx's pill lozenge, which reads as too heavy
// repeated twice in a results list.
function ResultsSectionLabel({ children }) {
  return (
    <Text fontSize="12px" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase" color="gray.400" mb={2}>
      {children}
    </Text>
  );
}

// Matching FAQs answer the query directly, in place, via the same accordion pattern
// FaqSlice.jsx uses to render them inside an article — there's no dedicated FAQ page to
// link to (a FAQ can appear on several help pages, or none), and the answer being one
// click away here is more useful than sending the user off to find it themselves.
function FaqResultsAccordion({ faqs }) {
  return (
    <Accordion allowMultiple>
      {faqs.map((faq) => {
        const question = faq.data?.question;
        const answer = faq.data?.answer;
        if (!question) return null;
        return (
          <AccordionItem key={faq.id} border="none" borderBottom="1px solid" borderColor="gray.100">
            <AccordionButton px={0} py={3}>
              <Text flex="1" textAlign="left" fontWeight="600" fontSize="body.l" color="#0E3137">
                {question}
              </Text>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={4} pt={0}>
              {answer?.length > 0 && <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: asHTML(answer) }} />}
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

function SearchResultsPanel({ query, results, faqResults, onExitSearch, onSelectResult }) {
  const totalCount = results.length + faqResults.length;
  return (
    <Box maxW={CONTENT_MAX_W} mx="auto" w="100%" px={6} py={8}>
      <BackLink onClick={onExitSearch}>Back</BackLink>
      <Text fontSize="24px" fontWeight="700" color="#0E3137" mt={4} mb={1}>
        Search results for &ldquo;{query}&rdquo;
      </Text>
      <Text fontSize="body.s" color="gray.500" mb={6}>
        {faqResults.length} {faqResults.length === 1 ? "FAQ" : "FAQs"}, {results.length}{" "}
        {results.length === 1 ? "article" : "articles"} found
      </Text>

      {totalCount === 0 && <Text color="gray.500">Nothing matches your search.</Text>}

      {faqResults.length > 0 && (
        <Box mb={results.length > 0 ? 8 : 0}>
          <ResultsSectionLabel>FAQs</ResultsSectionLabel>
          <FaqResultsAccordion faqs={faqResults} />
        </Box>
      )}

      {results.length > 0 && (
        <Box>
          <ResultsSectionLabel>Articles</ResultsSectionLabel>
          <VStack align="stretch" gap={2}>
            {results.map((article) => (
              <Flex
                key={article.uid}
                direction="column"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={4}
                cursor="pointer"
                _hover={{ borderColor: "lime" }}
                onClick={() => onSelectResult(article.uid)}
              >
                <Text fontSize="body.l" fontWeight="600" color="#0E3137">
                  {getArticleDisplayTitle(article)}
                </Text>
                {article.data.summary && (
                  <Text fontSize="body.s" color="gray.500" mt={1}>
                    {article.data.summary}
                  </Text>
                )}
              </Flex>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

// Slice types that render as full-bleed banners (no page padding, no max-width
// column) — processimage has its own dark hero-gradient section background
// (matches the website's slice, see ProcessimageSlice.jsx), so it needs to bleed
// edge-to-edge like the hero slices rather than sit inside the narrow content column.
const FULL_BLEED_SLICE_TYPES = new Set(["home_hero", "secondaryhero", "processimage"]);

// Non-full-bleed content (guide text, cards, FAQ links) sits in the same narrow,
// centered column used elsewhere in the app (e.g. AnswerDetailPage/FoundationAnswerDetailPage
// both use maxW="900px" mx="auto") — only the hero/footer banners go full-bleed.
const CONTENT_MAX_W = "900px";

// Entry-article-only: the first 3 "link" slices (Carbon Accounting / Sustainability
// Reporting (AIR) / Antaia Admins) sit side by side as columns instead of stacked full
// width — this is a layout choice specific to how the Entry article reads, not a
// general rule for the "link" slice type, so it's gated on `isEntry` rather than
// living in LinkSlice.jsx itself. Wider than CONTENT_MAX_W since 3 columns of link
// cards need more horizontal room than the single-column article body.
const LINK_COLUMNS_MAX_W = "1200px";
const LINK_COLUMNS_COUNT = 3;

// Icons for the 3 column headings — same icons used for these areas in the main
// left nav (Reporting (AIR) = NotePencil, Settings/Admin = GearSix in
// config/paths.js sideBarPages). There's no matching Carbon nav icon in this app
// today, so Plant stands in for it (matches the sprout icon in the design).
// Matched by heading text (case-insensitive substring) since the heading is
// free-text content authored in Prismic, not a stable slug/key.
const COLUMN_HEADING_ICONS = [
  { match: /carbon/i, icon: Plant },
  { match: /report|air/i, icon: NotePencil },
  { match: /admin/i, icon: GearSix },
];

function getColumnHeadingIcon(heading) {
  return COLUMN_HEADING_ICONS.find((entry) => entry.match.test(heading || ""))?.icon;
}

// AnimatedPageWrapper's Container has Chakra's default px="4" / paddingBlock="5"
// baked in (see @chakra-ui/theme Container baseStyle) even with maxW="100%" —
// cancel it out on full-bleed blocks so they reach the true viewport edge.
// `topGap` adds extra breathing room above the block (on top of the isFirst
// cancellation) — used for the secondary hero, which always renders last and
// otherwise sits right against the preceding slice's bottom padding.
function FullBleedBlock({ isFirst, isLast, topGap, children }) {
  return (
    <Box mx={-4} mt={isFirst ? -5 : topGap || 0} mb={isLast ? -5 : 0}>
      {children}
    </Box>
  );
}

// Extra vertical gap above the secondary hero band specifically — it always
// renders as the last slice on every Help page, and the default spacing left
// it sitting right up against the preceding slice's content.
const SECONDARY_HERO_TOP_GAP = 12;

const HomeHeroComponent = SLICE_COMPONENTS.home_hero;

function HelpArticleDetail({
  article,
  isEntry,
  onBack,
  onNavigate,
  searchQuery,
  onSearchChange,
  isSearching,
  searchResults,
  faqSearchResults,
  onExitSearch,
  onSelectResult,
}) {
  const allSlices = article.data.slices || [];
  const hasBody = article.data.body?.length > 0;

  // The hero always renders as the page banner at the true top of the page,
  // full-bleed, ABOVE the "Back to Help" link — regardless of where it's
  // authored in the Slice Zone order. Everything else renders below "Back to
  // Help" in its authored order.
  const heroIndex = allSlices.findIndex((slice) => slice.slice_type === "home_hero");
  const heroSlice = heroIndex >= 0 ? allSlices[heroIndex] : null;
  const restSlices = heroIndex >= 0 ? allSlices.filter((_, i) => i !== heroIndex) : allSlices;

  return (
    <VStack align="stretch" gap={0}>
      {heroSlice && (
        <FullBleedBlock isFirst>
          <HomeHeroComponent
            slice={heroSlice}
            onNavigate={onNavigate}
            searchValue={searchQuery}
            onSearchChange={onSearchChange}
          />
        </FullBleedBlock>
      )}

      {isSearching ? (
        <SearchResultsPanel
          query={searchQuery}
          results={searchResults}
          faqResults={faqSearchResults}
          onExitSearch={onExitSearch}
          onSelectResult={onSelectResult}
        />
      ) : (
        <>
          {!isEntry && (
            <Box maxW={CONTENT_MAX_W} mx="auto" w="100%" px={6} pt={8} pb={2}>
              <HStack justify="space-between" align="center">
                <HStack
                  as="button"
                  onClick={onBack}
                  cursor="pointer"
                  gap={2}
                  color="#5A8A1A"
                  fontSize="14px"
                  _hover={{ color: "#4a7516" }}
                  transition="color 0.15s"
                >
                  <ArrowLeft size={16} />
                  <Text color="inherit" fontSize="inherit">
                    Back to Help
                  </Text>
                </HStack>
                {article.last_publication_date && (
                  <Text fontSize="14px" color="gray.400">
                    Last updated:{" "}
                    {new Date(article.last_publication_date).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </Text>
                )}
              </HStack>
            </Box>
          )}

          {(() => {
            // On the Entry article only: group the first LINK_COLUMNS_COUNT "link"
            // slices into one 3-column row, in their original relative order. Any
            // further "link" slices (e.g. FAQs) and every other slice type render
            // exactly as before, in their original position in the Slice Zone.
            const LinkComponent = SLICE_COMPONENTS.link;
            let linkSlicesSeen = 0;
            const columnSlices = [];
            const rendered = [];

            restSlices.forEach((slice, index) => {
              const isColumnCandidate = isEntry && slice.slice_type === "link" && linkSlicesSeen < LINK_COLUMNS_COUNT;
              if (isColumnCandidate) {
                linkSlicesSeen += 1;
                columnSlices.push(slice);
                // Once the last of the 3 has been collected, render the row in the
                // position of that last slice — preserves the slices' authored order
                // relative to whatever comes before/after them.
                if (linkSlicesSeen === LINK_COLUMNS_COUNT) {
                  rendered.push(
                    <Box key="link-columns" maxW={LINK_COLUMNS_MAX_W} mx="auto" px={6} py={4}>
                      <SimpleGrid columns={{ base: 1, md: LINK_COLUMNS_COUNT }} gap={6} alignItems="start">
                        {columnSlices.map((columnSlice) => (
                          <Box
                            key={columnSlice.id}
                            border="1px solid"
                            borderColor="limeTint.300"
                            borderRadius="lg"
                            p={5}
                          >
                            <LinkComponent
                              slice={columnSlice}
                              onNavigate={onNavigate}
                              headingIcon={getColumnHeadingIcon(columnSlice.primary?.heading)}
                              isHomePage={isEntry}
                            />
                          </Box>
                        ))}
                      </SimpleGrid>
                    </Box>
                  );
                }
                return;
              }

              const SliceComponent = SLICE_COMPONENTS[slice.slice_type];
              if (!SliceComponent) {
                console.warn(`No renderer for help_article slice type "${slice.slice_type}"`);
                return;
              }
              const isFullBleed = FULL_BLEED_SLICE_TYPES.has(slice.slice_type);
              const isLastBlock = index === restSlices.length - 1 && !hasBody;
              const content = (
                <Box
                  maxW={isFullBleed ? "100%" : CONTENT_MAX_W}
                  mx="auto"
                  px={isFullBleed ? 0 : 6}
                  py={isFullBleed ? 0 : 4}
                >
                  <SliceComponent
                    slice={slice}
                    onNavigate={onNavigate}
                    {...(slice.slice_type === "link" ? { isHomePage: isEntry } : {})}
                  />
                </Box>
              );
              rendered.push(
                isFullBleed ? (
                  <FullBleedBlock
                    key={slice.id}
                    isLast={isLastBlock}
                    topGap={slice.slice_type === "secondaryhero" ? SECONDARY_HERO_TOP_GAP : 0}
                  >
                    {content}
                  </FullBleedBlock>
                ) : (
                  <Box key={slice.id}>{content}</Box>
                )
              );
            });

            return rendered;
          })()}

          {hasBody && (
            <Box
              maxW={CONTENT_MAX_W}
              mx="auto"
              w="100%"
              px={6}
              py={4}
              className="help-article-body"
              dangerouslySetInnerHTML={{ __html: asHTML(article.data.body) }}
            />
          )}
        </>
      )}
    </VStack>
  );
}

export default WithAuthorization(HelpPage);
